export enum SyncStatus{
    FAILED = 'failed',
    COMPLETED = 'completed',
    PROCESSING = 'processing',
    SYNCING = 'processing'
}

export enum MessageStatus{
    FAILED = 'failed',
    COMPLETED = 'completed',
    PROCESSING = 'processing',
    // Entitlement refusal, not an error: the request was understood and denied.
    // Matches the frame the download-tax-report handler already sends.
    DENIED = 'denied'
}

export type TaxEvent = {
  asset: string;
  cost: number;
  datetime: string;
  disposal_type: string;
  ext_txn_id?: string;
  fees: number;
  gain: number;
  proceeds: number;
  quantity: number;
  tax_year: string;
  txn_id: number;
};

export enum TaxType{
    CAPITAL_GAIN = "capital-gain",
    INCOME = "income",
    CONTRACT_INCOME = "contract-income"
}

export enum AccountingMethod {
  FIFO = "FIFO",
  LIFO = "LIFO",
  HIFO = "HIFO",
}

export enum TaxJurisdiction {
  US = "United States",
  UK = "United Kingdom",
}

export enum CapitalTerm{
    ALL = "all",
    LONG_TERM = "long-term",
    SHORT_TERM = 'short-term'
}

export interface Txn {
  buyCoinGeckoId?: string;
  buyasset?: string;
  buyqty?: number;
  datetime?: string;
  exchange?: string;
  ext_txn_id?: string;
  feeasset?: string;
  feeqty?: number;
  price?: number;
  sellCoinGeckoId?: string;
  sellasset?: string;
  sellqty?: number;
  side?: string;
  timestamp?: number;
  tradeid?: string;
  type?: string;
  wallet?: string;
  bridge_info?: {
    id: number;
    status: string;
    bridge_name: string;
    confidence_level: number;
    bridge_contract_id: number;
  } | null;
}

export interface AuditTxn extends Txn {
  created_at?: string;
  error_category?: string;
  error_data?: {
    buyasset?: string;
    buyqty?: number;
    buyvalue?: number;
    buyvalueasset?: string;
    calculated_fee_value?: number;
    calculated_value?: number;
    datetime?: string;
    exchange?: string;
    feeasset?: string;
    feeqty?: number;
    feevalue?: number;
    final_feevalue?: number;
    final_value?: number;
    id?: number;
    importtype?: string;
    price?: number;
    sellasset?: string;
    sellqty?: number;
    sellvalue?: number;
    sellvalueasset?: string;
    side?: string;
    timestamp?: number;
    tradeid?: string;
    type?: string;
    valueasset?: string;
    valuenote?: string;
    valuerate?: number;
    wallet?: string;
  };
  error_context?: {
    asset?: string;
    ledger?: Array<{
      id: number;
      timestamp: number;
      type: string;
      received: number;
      spent: number;
      balance: number;
      is_problem_txn?: boolean;
    }>;
    balance_before_txn?: number;
    sell_amount?: number;
    required_deposit?: number;
    required_amount?: number;
    suggested_resolution?: string;
    txn_type?: string;
    missing_fields?: string[];
    txn_summary?: any;
    field_name?: string;
    current_value?: any;
    issue?: string;
    // Receive detection fields for smart imbalance resolution suggestions
    receives_would_resolve?: boolean;
    receive_is_only_source?: boolean;
    potential_income_txns?: Array<{
      id: number;
      amount: number;
      timestamp: number;
      type: string;
      exchange?: string | null;
    }>;
    total_income?: number;
    total_received?: number;
  };
  error_msg?: string;
  error_type?: string;
  id?: number;
  txn_id?: string;
  updated_at?: string;
  user_id?: string;
}

export enum AuditType {
  All = "all",
  MissingFiat = "missing-fiat",
  MissingFees = "missing-fees",
  SellBuyImbalance = "sell-buy-imbalance",
  MissingDeposits = "missing-deposits",
  AbnormalTransactionVolume = "abnormal-transaction-volume",
  DuplicateTransactions = "duplicate-transactions",
  OrphanTransactions = "orphan-transactions",
  DataConsistencyErrors = "data-consistency-errors",
  UnusualUserActivity = "unusual-user-activity",
}

export enum AlertVariants {
  Warning = "warning",
  Info = "info",
  Success = "success",
  Error = "error",
}

/**
 * Lifecycle stages for dashboard personalization
 */
export enum LifecycleStage {
  NEW = "NEW", // No sources, no txns
  CONNECTING = "CONNECTING", // Source added, first sync in progress
  BOOTSTRAPPED = "BOOTSTRAPPED", // Sources synced, some txns, no tax settings
  ACTIVE_LIGHT = "ACTIVE_LIGHT", // <500 txns, few issues
  ACTIVE_HEAVY = "ACTIVE_HEAVY", // ≥500 txns or ≥5 chains or DeFi detected
  FILING_MODE = "FILING_MODE", // Within 30 days of deadline
  POWER_USER = "POWER_USER", // Opted-in compact mode
}

/**
 * Overlays that can stack on any lifecycle stage
 */
export enum DashboardOverlay {
  BROKEN_CONNECTIONS = "BROKEN_CONNECTIONS",
  BRIDGE_REVIEW_NEEDED = "BRIDGE_REVIEW_NEEDED",
  AUDIT_FLAGS = "AUDIT_FLAGS",
  DATA_DRIFT = "DATA_DRIFT",
  UNPRICED_ASSETS = "UNPRICED_ASSETS",
}
// export type Txn = {
//   month: string;
//   tradeid: string;
//   datetime: string;
//   type: "buy" | "sell";
//   buyasset: string;
//   sellasset: string;
//   buyqty: number;
//   sellqty: number;
//   buyvalue: number;
//   sellvalue: number;
//   side: "buy" | "sell";
//   price: string;
//   exchange: string;
//   ext_txn_id: string;
// };

// display names
export enum Exchange {
  OKX = "OKX",
  Bybit = "Bybit",
  Upbit = "Upbit",
  Kraken = "Kraken",
  "Gate.io" = "Gate.io",
  HTX = "HTX",
  Bitfinex = "Bitfinex",
  KuCoin = "KuCoin",
  Coinbase = "Coinbase",
  Binance = "Binance",
}
export enum ExchangeKey {
  OKX = "okx",
  Bybit = "bybit",
  Upbit = "upbit",
  Kraken = "kraken",
  Gateio = "gateio",
  HTX = "htx",
  Bitfinex = "bitfinex",
  KuCoin = "kucoin",
  Coinbase = "coinbase",
  Binance = "binance",
}

export type ExchangeConnectionInfo = {
  exchange: Exchange;
  exchangename: string;
  type: string; // api or oauth
};

export enum TxnType {
  SEND = "send",
  RECEIVE = "receive",
  DEPOSIT = "deposit",
  WITHDRAWAL = "withdrawal",
  TRADE = "trade",
  BUY = "buy",
  SELL = "sell",
  INTEREST = "interest",
  STAKING_REWARD = "staking reward",
  ADVANCED_TRADE_FILL = "advanced trade fill",
  ADVANCED_TRADE_ORDER = "advanced trade order",
  GIFT_SENT = "gift_sent",
  GIFT_RECEIVED = "gift_received",
  DONATION = "donation",
}

export enum FormattedTxnType {
  DEPOSIT = "Deposit",
  WITHDRAWAL = "Withdrawal",
  TRADE = "Trade",
  BUY = "Buy",
  SELL = "Sell",
  INTEREST = "Interest",
  GIFT_SENT = "Gift Sent",
  GIFT_RECEIVED = "Gift Received",
  DONATION = "Donation",
}

export interface TradeItemProps {
  id: string;
  tradeid: string;
  type: string;
  exchange: string;
  timestamp: string;
  datetime: string;
  buyasset: string;
  buyqty: string;
  sellasset: string;
  sellqty: string;
  feeqty: string;
  feeasset: string;
  wallet: string;
  side: string;
  userId: string;
  screen: "sm" | "md" | "lg";
  // for showing emtpy row i.e. no results
  isEmpty: boolean;
  text: string;
}

export const exchangeInfoList: Record<Exchange, ExchangeWithDisplayName> = {
  [ExchangeKey.Binance]: { exchangename: "Binance", exchange: "binance" },
  [ExchangeKey.Bitfinex]: { exchangename: "Bitfinex", exchange: "bitfinex" },
  [ExchangeKey.Bybit]: { exchangename: "Bybit", exchange: "bybit" },
  [ExchangeKey.Coinbase]: { exchangename: "Coinbase", exchange: "coinbase" },
  [ExchangeKey.Gateio]: { exchangename: "Gate.io", exchange: "gateio" },
  [ExchangeKey.HTX]: { exchangename: "HTX", exchange: "htx" },
  [ExchangeKey.Kraken]: { exchangename: "Kraken", exchange: "kraken" },
  [ExchangeKey.KuCoin]: { exchangename: "KuCoin", exchange: "kucoin" },
  [ExchangeKey.OKX]: { exchangename: "OKX", exchange: "okx" },
  [ExchangeKey.Upbit]: { exchangename: "Upbit", exchange: "upbit" },
};
export type ExchangeWithDisplayName = Exchange & {
  displayName: string;
  primaryKey: string;
};

const exchangeMap: Record<Exchange, ExchangeWithDisplayName> = {
  [Exchange.OKX]: { displayName: "OKX", primaryKey: "okx" },
  [Exchange.Bybit]: { displayName: "Bybit", primaryKey: "bybit" },
  [Exchange.Upbit]: { displayName: "Upbit", primaryKey: "upbit" },
  [Exchange.Kraken]: { displayName: "Kraken", primaryKey: "kraken" },
  [Exchange["Gate.io"]]: { displayName: "Gate.io", primaryKey: "gateio" },
  [Exchange.HTX]: { displayName: "HTX", primaryKey: "htx" },
  [Exchange.Bitfinex]: { displayName: "Bitfinex", primaryKey: "bitfinex" },
  [Exchange.KuCoin]: { displayName: "KuCoin", primaryKey: "kucoin" },
  [Exchange.Coinbase]: { displayName: "Coinbase", primaryKey: "coinbase" },
  [Exchange.Binance]: { displayName: "Binance", primaryKey: "binance" },
};

export const exchanges: ExchangeWithDisplayName[] = Object.values(exchangeMap);

export const exchangeByDisplayName: Record<string, ExchangeWithDisplayName> =
  exchanges.reduce((acc, exchange) => {
    acc[exchange.displayName] = exchange;
    return acc;
  }, {});

export const exchangeByPrimarykey: Record<string, ExchangeWithDisplayName> =
  exchanges.reduce((acc, exchange) => {
    acc[exchange.primaryKey] = exchange;
    return acc;
  }, {});

// Example usage
const selectedExchangeByName: ExchangeWithDisplayName =
  exchangeByDisplayName["OKX"];
console.log(selectedExchangeByName.displayName); // Output: "OKX"
console.log(selectedExchangeByName.primaryKey); // Output: "okx"

const selectedExchangeByPrimarykey: ExchangeWithDisplayName =
  exchangeByPrimarykey["okx"];
console.log(selectedExchangeByPrimarykey.displayName); // Output: "OKX"
console.log(selectedExchangeByPrimarykey.primaryKey); // Output: "okx"
